import { type FC, type MouseEvent, useRef, useState } from 'react'
import classnames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useIsElementTruncated } from 'shared/hooks'
import { Avatar, Icon, type IDropdownItemProps, Tooltip, Typography } from 'shared/ui'
import { getAriaLabel } from 'shared/lib'
import styles from './styles.module.scss'

export const DropdownCardDefault: FC<IDropdownItemProps> = observer(
  ({ onChange, item, parent, isHover }) => {
    const [hover, setHover] = useState(false)
    const titleRef = useRef<HTMLSpanElement>(null)
    const isLabelTruncated = useIsElementTruncated(titleRef, [item.label, item.labelContent])
    const { onClick } = item
    const eventMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (e.button !== 0) return e.preventDefault()
      if (item.disabled) return e.preventDefault()
      if ((item.children?.length || item?.customChildren) && !item.isCardClick)
        return e.preventDefault()

      onClick?.()
      onChange?.(item, parent)
    }

    const eventClick = (e: MouseEvent) => {
      e.stopPropagation()
    }

    const onMouseEnter = () => {
      setHover(true)
    }
    const onMouseLeave = () => {
      setHover(false)
    }

    const label = item.labelContent || item.label

    const renderActive = () => {
      const isActiveValueBoolean = typeof item.activeValue === 'boolean'

      if (!isActiveValueBoolean && item.activeValue) {
        return (
          <div className={classnames(styles.icon__right, styles.active)}>
            <div className={styles.activeValue}>{item.activeValue}</div>
          </div>
        )
      }

      return null
    }

    const content = (
      <div
        className={classnames(styles.item, item.className, {
          [styles.item__disabled]: item.disabled,
          [styles['item--hover']]: isHover,
          [styles['item-selected']]: item.selected,
          ['hasDropdownCardChild']: parent,
        })}
        style={{
          height: item.height,
        }}
        onClick={eventClick}
        onMouseDown={eventMouseDown}
        data-id={item.id}
      >
        <div className={styles.itemWrap} aria-label={getAriaLabel(item.ariaLabel || '')}>
          {(item.iconL || item.customIconL) && (
            <div className={styles.icon}>
              {item.iconL ? (
                <Icon
                  icon={item.iconL}
                  color={
                    item.customFields?.leftIconColor ? String(item.customFields?.leftIconColor) : ''
                  }
                />
              ) : (
                item.customIconL
              )}
            </div>
          )}
          {item.avatarProps && (
            <div className={styles.icon}>
              <Avatar {...item.avatarProps} />
            </div>
          )}
          <div
            className={classnames(styles.title, {
              [styles.title__active]: item.activeValue,
              [styles.withRightContent]: item.labelRight,
            })}
            style={{
              width: item.labelWidth,
              ...(item.customFields?.titleColor && {
                color: String(item.customFields.titleColor),
              }),
            }}
          >
            <span className={styles.labelWrapper} ref={titleRef}>
              {label}
            </span>
            {item.labelCount && (
              <Typography
                variant={'body-xs-medium'}
                color={'var(--content-neutral-primary)'}
                ariaLabel={'labelCount'}
              >
                {item.labelCount}
              </Typography>
            )}
            {item.labelBrackets && (
              <Typography
                variant={'body-md-regular'}
                color={'var(--content-primary-disabled)'}
                ariaLabel={'labelBrackets'}
                className={styles.labelBrackets}
              >
                ({item.labelBrackets})
              </Typography>
            )}
            {item.description && (
              <Typography
                ariaLabel={'description'}
                variant={'body-xs-regular'}
                color={'tertiary'}
                tag={'div'}
                className={styles.desc}
              >
                {item.description}
              </Typography>
            )}
          </div>
          {item.iconLabel && (
            <div className={styles.icon__iconLabel}>
              <Icon icon={item.iconLabel} />
            </div>
          )}
        </div>
        <div className={classnames(styles.itemWrap, styles.rightContent)}>
          {renderActive()}
          {item.iconR && (
            <div className={styles.icon__right}>
              <Icon
                icon={item.iconR}
                color={
                  item.customFields?.rightIconColor ? String(item.customFields?.rightIconColor) : ''
                }
              />
            </div>
          )}
          {item.labelRight && !item.tooltipLabelRightProps && (
            <Typography
              variant={item.labelRightVariant || 'body-xs-regular'}
              color={'var(--content-primary-tertiary)'}
              ariaLabel={'labelRight'}
              ellipsis
              className={item.labelRightClassName}
            >
              {item.labelRight}
            </Typography>
          )}
          {item.labelRight && item.tooltipLabelRightProps && (
            <>
              <Tooltip {...item.tooltipLabelRightProps} open={hover}>
                <div className={styles.tooltipLabelRight} />
              </Tooltip>
              <div
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={styles.wrapLabelRight}
              >
                <Typography
                  variant={'body-xs-regular'}
                  color={'var(--content-primary-tertiary)'}
                  ariaLabel={'labelRight'}
                  ellipsis
                >
                  {item.labelRight}
                </Typography>
              </div>
            </>
          )}
          {item.hotKey && (
            <Typography
              tag={'div'}
              className={styles.hotKey}
              variant={'body-xs-medium'}
              ariaLabel={'hotKey'}
              width={16}
            >
              {item.hotKey}
            </Typography>
          )}
          {item.tooltipQuestionProps && (
            <div className={styles.tooltip}>
              <Tooltip {...item.tooltipQuestionProps}>
                <Icon icon={'question'} {...item.iconQuestionProps} />
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    )

    if (item.link) {
      return (
        <a href={item.link} target={'_blank'} rel='noreferrer' className={'noTextDecoration'}>
          {content}
        </a>
      )
    }
    if (item.tooltipProps || isLabelTruncated) {
      return (
        <Tooltip fullWidth placement='left' label={item.label} {...item.tooltipProps}>
          {content}
        </Tooltip>
      )
    }

    return content
  }
)
